import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../Datas/siteConfig";
import "../css/terms.scss";

const termSections = [
  ["scope", "Scope of these terms"],
  ["orders", "Orders and acceptance"],
  ["products", "Products and availability"],
  ["pricing", "Prices and payment"],
  ["taxes", "Tax, duties and currency"],
  ["delivery", "Delivery"],
  ["risk", "Ownership and risk"],
  ["returns", "Cancellations and returns"],
  ["faulty", "Faulty goods"],
  ["promotions", "Promotions"],
  ["use", "Using this website"],
  ["ip", "Intellectual property"],
  ["privacy", "Privacy and browser storage"],
  ["liability", "Responsibility"],
  ["law", "Law and changes"],
  ["contact", "Questions"],
];

function Terms() {
  const { brandName, contact, currency } = siteConfig;

  return (
    <div className="terms-page">
      <nav className="terms-page__breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Terms and conditions</span>
      </nav>

      <header className="terms-page__header">
        <p className="terms-page__eyebrow">Shopping with us</p>
        <h1>Terms and conditions</h1>
        <p>
          These terms set out the practical and legal basis for using this
          website and placing an order with {brandName}.
        </p>
        <span>Last updated: 23 October 2026</span>
      </header>

      <main className="terms-page__layout">
        <aside className="terms-page__contents" aria-label="On this page">
          <h2>On this page</h2>
          <ol>
            {termSections.map(([id, label]) => (
              <li key={id}>
                <a href={"#" + id}>{label}</a>
              </li>
            ))}
          </ol>
          <div>
            <strong>Need help with an order?</strong>
            <Link to="/contact">Contact customer care</Link>
          </div>
        </aside>

        <article className="terms-page__article">
          <section className="terms-page__intro-note">
            <strong>Please read before ordering</strong>
            <p>
              By using this website or submitting an order, you agree to the
              terms that apply at that time. Nothing here limits rights that
              cannot legally be excluded.
            </p>
          </section>

          <section id="scope">
            <p className="terms-page__section-label">01</p>
            <h2>Scope of these terms</h2>
            <p>
              These terms apply to consumer purchases made through this online
              store. They explain how a contract is formed, how products are
              supplied and how concerns are handled. Separate notices may apply
              to promotions or specific services; where they conflict, the more
              specific notice applies to that activity.
            </p>
            <p>
              You must be legally able to enter into a contract and provide
              accurate contact, billing and delivery information when ordering.
              The legal seller is the entity identified on the applicable order
              confirmation, receipt or invoice. See the
              {" "}<Link to="/legal-notice">legal notice</Link> for storefront
              contact details.
            </p>
          </section>

          <section id="orders">
            <p className="terms-page__section-label">02</p>
            <h2>Orders and acceptance</h2>
            <p>
              Adding an item to a basket does not reserve it. Submitting checkout
              details is an offer to buy. An automated acknowledgement confirms
              receipt, while acceptance occurs when we send a dispatch
              confirmation unless applicable law requires otherwise.
            </p>
            <p>
              We may decline or cancel an order before dispatch if a product is
              unavailable, payment cannot be authorised, information appears
              incorrect, purchase limits are exceeded or fraud is reasonably
              suspected. Any captured payment for a cancelled item will be
              returned.
            </p>
          </section>

          <section id="products">
            <p className="terms-page__section-label">03</p>
            <h2>Products and availability</h2>
            <p>
              We work to present accurate descriptions, colours, measurements
              and images. Screen settings and manufacturing tolerances mean
              minor variations may occur. Product availability can change while
              an order is being completed.
            </p>
            <p>
              Care, fit and safety instructions supplied with a product should
              be followed. Outdoor performance depends on correct use, layering,
              maintenance and conditions.
            </p>
          </section>

          <section id="pricing">
            <p className="terms-page__section-label">04</p>
            <h2>Prices and payment</h2>
            <p>
              Store prices are displayed in {currency} unless clearly stated
              otherwise. The checkout summary shows the product total, delivery
              cost and any applicable discount before payment is submitted.
            </p>
            <p>
              If an obvious pricing error is discovered, we may contact you for
              instructions or cancel the affected item and refund payment.
              Payment methods shown at checkout are processed subject to the
              provider's checks and terms.
            </p>
          </section>

          <section id="taxes">
            <p className="terms-page__section-label">05</p>
            <h2>Tax, duties and currency</h2>
            <p>
              Payments are submitted in {currency}. A bank may apply its own
              conversion rate or fee if your account uses a different currency.
              Tax treatment depends on the destination and seller identified on
              the order documents.
            </p>
            <p>
              International deliveries may attract import tax, duty, brokerage
              or carrier charges. Where these are not collected at checkout, the
              recipient may be responsible for them to the extent permitted by
              law. Read the
              {" "}<Link to="/pricing-tax-policy">pricing, tax and duties policy</Link>.
            </p>
          </section>

          <section id="delivery">
            <p className="terms-page__section-label">06</p>
            <h2>Delivery</h2>
            <p>
              Available services, charges and estimated dates depend on the
              destination and are shown at checkout. Estimates are not guaranteed
              where delay results from events beyond reasonable control, but we
              will provide updates and available remedies.
            </p>
            <p>
              You are responsible for providing an accessible, correct delivery
              address. See our <Link to="/shipping">delivery information</Link>
              {" "}
              for service guidance and tracking details.
            </p>
          </section>

          <section id="risk">
            <p className="terms-page__section-label">07</p>
            <h2>Ownership and risk</h2>
            <p>
              Risk passes as required by applicable consumer law, normally when
              you or a person you nominate receives the goods. Ownership passes
              when payment has been received in full, unless mandatory law says
              otherwise.
            </p>
            <p>
              If you arrange a carrier that we did not offer, different risk
              rules may apply from the time the goods are given to that carrier.
            </p>
          </section>

          <section id="returns">
            <p className="terms-page__section-label">08</p>
            <h2>Cancellations and returns</h2>
            <p>
              Eligible online purchases may be cancelled or returned in
              accordance with applicable consumer law and our published policy.
              Some goods may be excluded where the law permits, including items
              altered to a customer's specification or sealed hygiene-sensitive
              goods once unsealed.
            </p>
            <p>
              Products should be kept in returnable condition while you decide.
              Full instructions, condition guidance and typical refund timing
              are available on the <Link to="/returns">returns page</Link>.
            </p>
          </section>

          <section id="faulty">
            <p className="terms-page__section-label">09</p>
            <h2>Faulty, damaged or misdescribed goods</h2>
            <p>
              Change-of-mind conditions do not remove legal remedies for goods
              that are faulty, damaged on arrival or not as described. Stop
              using a product if continued use may be unsafe and contact us with
              the order number and evidence that reasonably helps assessment.
            </p>
            <p>
              Depending on the circumstances and applicable law, a remedy may
              be repair, replacement, price reduction or refund. See our
              {" "}<Link to="/warranty-policy">warranty and faulty goods policy</Link>.
            </p>
          </section>

          <section id="promotions">
            <p className="terms-page__section-label">10</p>
            <h2>Promotions</h2>
            <p>
              Discount codes and offers may have eligibility, product, territory,
              date or usage restrictions. Unless an offer says otherwise, only
              one code may be used per order, codes have no cash value and cannot
              be applied after checkout.
            </p>
            <p>
              If a qualifying product is returned, any refund reflects the amount
              actually paid and the promotion may be recalculated across the
              remaining order.
            </p>
          </section>

          <section id="use">
            <p className="terms-page__section-label">11</p>
            <h2>Using this website</h2>
            <p>
              Website content is provided for personal shopping and information.
              You must not interfere with security, introduce harmful code,
              scrape at disruptive scale, attempt unauthorised access or use
              content in a way that infringes intellectual-property rights.
            </p>
            <p>
              Links to third-party services are provided for convenience. Their
              availability, content and data practices are controlled by those
              providers.
            </p>
          </section>

          <section id="ip">
            <p className="terms-page__section-label">12</p>
            <h2>Intellectual property</h2>
            <p>
              Storefront materials may be viewed and saved for personal shopping
              and order records. They may not be republished, sold, used as
              another brand or copied at scale without permission. Third-party
              trade marks and product materials remain the property of their
              respective owners.
            </p>
            <p>
              Rights holders can use the notice process in our
              {" "}<Link to="/intellectual-property">intellectual property policy</Link>.
            </p>
          </section>

          <section id="privacy">
            <p className="terms-page__section-label">13</p>
            <h2>Privacy and browser storage</h2>
            <p>
              Personal information is handled as described in the
              {" "}<Link to="/privacy">privacy policy</Link>. Basket, wishlist and
              latest-order information may be kept in browser storage as listed
              in the <Link to="/cookies">cookie policy</Link>.
            </p>
          </section>

          <section id="liability">
            <p className="terms-page__section-label">14</p>
            <h2>Responsibility</h2>
            <p>
              We are responsible for foreseeable loss caused by our breach of
              these terms or failure to use reasonable care and skill. We do not
              exclude responsibility where doing so would be unlawful, including
              for fraud or personal injury caused by negligence.
            </p>
            <p>
              The store is intended for consumers. We are not responsible for
              business losses such as lost profit, revenue or opportunity arising
              from consumer use of the website or products.
            </p>
          </section>

          <section id="law">
            <p className="terms-page__section-label">15</p>
            <h2>Applicable law, severability and changes</h2>
            <p>
              Mandatory consumer protections and jurisdiction rules for your
              place of residence continue to apply. Subject to those rules,
              these terms and non-contractual disputes are governed by the law
              applicable to the seller identified on the order document.
            </p>
            <p>
              If a provision is unenforceable, the remaining provisions continue.
              A delay in enforcing a right is not a waiver. Updated terms apply
              to future use and orders, not retroactively to change an accepted
              purchase without a lawful basis.
            </p>
          </section>

          <section id="contact">
            <p className="terms-page__section-label">16</p>
            <h2>Questions and concerns</h2>
            <p>
              Contact us first so we can try to resolve a concern promptly. Use
              our <Link to="/contact">contact form</Link>, email
              {" "}
              <a href={"mailto:" + contact.email}>{contact.email}</a>. Our
              support hours are {contact.hours}.
            </p>
            <address>
              <strong>{brandName}</strong>
              <span>{contact.address}</span>
            </address>
            <p>
              You can also read our <Link to="/complaints-policy">complaints policy</Link>
              {" "}or browse the <Link to="/policies">policy centre</Link> for
              topic-specific information.
            </p>
          </section>

          <footer className="terms-page__article-footer">
            <span>Related information</span>
            <Link to="/privacy">Privacy policy</Link>
            <Link to="/shipping">Delivery information</Link>
            <Link to="/returns">Returns policy</Link>
          </footer>
        </article>
      </main>
    </div>
  );
}

export default Terms;
